import { type FC, useCallback, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { InStoreDeliveryStoreSelector } from "@jti/commerce";
import type { AddressInStoreDeliveryFieldValue } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { newrelic } from "@jti/fe-clients";
import type { FlotiqInStoreDeliveryStore } from "@jti/fe-common/types";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { FormFieldValues, ShippingFormFieldsConfig } from "@checkout-v2/forms";
import { useOnShippingAddressChange } from "@checkout-v2/forms/ShippingForm/renderer/utils/useOnShippingAddressChange";
import { useCartItems } from "@checkout-v2/shared/hooks/useCartItems";
import { useSetShippingAddressOnCart } from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";
import { getShippingAddressErrorMessage } from "@checkout-v2/shared/utils";
import { findInStoreDeliveryMethod } from "@checkout-v2/shared/utils/findInStoreDeliveryMethod";

type AddressInStoreDeliveryFieldProps = {
  formFieldsConfig: ShippingFormFieldsConfig;
  selectedShippingMethodCode: string;
};

export const AddressInStoreDeliveryField: FC<AddressInStoreDeliveryFieldProps> = ({
  formFieldsConfig,
  selectedShippingMethodCode,
}) => {
  const { __ } = useTranslations();
  const { name: fieldName } = formFieldsConfig.addressInStoreDelivery;
  const form = useFormContext<FormFieldValues<{ [fieldName]: AddressInStoreDeliveryFieldValue }>>();
  const { data: cartItems } = useCartItems();
  const { onShippingAddressChange } = useOnShippingAddressChange(formFieldsConfig);
  const { setShippingAddressOnCartState } = useSetShippingAddressOnCart();

  const fieldConfig = formFieldsConfig.addressInStoreDelivery;
  const { control, formState, getValues } =
    useFormContext<FormFieldValues<{ [fieldConfig.name]: AddressInStoreDeliveryFieldValue }>>();

  const selectedShippingMethod = findInStoreDeliveryMethod(fieldConfig.shippingMethods, selectedShippingMethodCode);
  const addressInStoreShippingFieldErrorMessage = formState.errors.addressInStoreDelivery?.message;

  const shippingAddressErrorMessage = getShippingAddressErrorMessage(setShippingAddressOnCartState.error, __);

  const onStoreSelect = useCallback(
    async (storeAddress: FlotiqInStoreDeliveryStore) => {
      form.clearErrors(fieldConfig.name);

      form.setValue(fieldConfig.name, storeAddress, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const isValid = await form.trigger(fieldConfig.name, { shouldFocus: true });

      if (!isValid || !storeAddress) {
        return;
      }

      await onShippingAddressChange(storeAddress);
    },
    [fieldConfig.name, form, onShippingAddressChange]
  );

  useEffect(function setShippingAddressOnCartOnInitialMount() {
    if (!selectedShippingMethod) {
      return;
    }

    const selectedStoreAddress = getValues(fieldConfig.name);

    if (!selectedStoreAddress) {
      return;
    }

    onShippingAddressChange(selectedStoreAddress).catch((error) => {
      newrelic.noticeError(error as Error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!selectedShippingMethod) {
    newrelic.noticeError(
      new Error(
        `InStoreDeliverySection: couldn't find shipping method for selected option: ${selectedShippingMethodCode}. Check shipping methods configuration.`
      )
    );
    return null;
  }

  return (
    <>
      {(shippingAddressErrorMessage || addressInStoreShippingFieldErrorMessage) && (
        <AlertBanner
          data-testid="info-banner-wrapper"
          tone="error"
          description={(shippingAddressErrorMessage || addressInStoreShippingFieldErrorMessage)!}
        />
      )}

      <Controller
        control={control}
        name={fieldConfig.name}
        rules={{
          required: fieldConfig.registerOptions?.required,
        }}
        render={({ field }) => (
          <InStoreDeliveryStoreSelector
            onStoreSelect={onStoreSelect}
            selectedStore={field.value}
            productsInCart={cartItems}
            description={selectedShippingMethod.description}
            storeLocatorConfiguration={selectedShippingMethod}
          />
        )}
      />
    </>
  );
};
